'use client';

import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/lib/auth-store';
import { createBrowserClient } from '@/lib/supabase/client';
import { SpriteTransformTesterPanel, spriteAdjustToTransform, useSpriteTuning } from '@/components/sprite-transform-tester';
import { useTranslations } from "next-intl"

// 13-frame walk cycle, cropped from the source sheet's 256x256 grid (see
// Glymling-climb.png in the branding assets — 5 cols x 3 rows, last row
// partial). Loops continuously for the whole climb, same as any idle/walk
// cycle would.
const CLIMB_FRAME_COUNT = 13;
const CLIMB_FRAMES = Array.from({ length: CLIMB_FRAME_COUNT }, (_, i) => `/glym/glymling-climb/frame${String(i + 1).padStart(2, '0')}.webp`);

// Starts straight at the starburst splash (12) and flattens into a puddle
// (13-17) -- an earlier version tried bridging from "standing" with extra
// lead-in frames, which was more complex than necessary. These frames are
// drawn for a horizontal ground splat (flat base at the bottom, spread
// left-right); rendered as-is against a vertical edge they'd look wrong,
// so they're rotated -90deg at render time (see the img's style below) so
// the flat base presses against the edge instead, reading as "splatted
// against the wall" rather than floating in open air.
const SPLAT_FRAMES = [12, 13, 14, 15, 16, 17].map((n) => `/glym/glymling-splat/frame${String(n).padStart(2, '0')}.webp`);

// The climb sheet isn't a seamless loop -- her body has real vertical
// climbing motion baked into the artwork itself (measured via each frame's
// alpha-weighted pixel centroid: frame 1 sits at y=126, frame 12 at y=109,
// a real ~17px upward drift), so looping frame 13 straight back to frame 1
// snapped her back down by that distance every cycle -- the reported
// "jolt". These are the per-frame downward counter-offsets (in the source
// sheet's 256px space, scaled to SPRITE_SIZE at render time) that cancel
// that drift so the walk cycle holds her at a stable vertical position and
// only the limb motion shows -- the real climbing motion is entirely the
// CSS keyframe's job, not the spritesheet's.
const CLIMB_FRAME_Y_OFFSET = [0, -0.64, -1.43, 1.17, 3.72, 7.74, 8.02, 6.85, 6.95, 10.34, 13.83, 17.42, 15.87];

// The real, shipped splat transform -- a super_admin can preview live
// adjustments to these exact numbers via the "Test Glym splat" panel
// without touching code, then report back the values that looked right.
const SPLAT_ADJUST_DEFAULT = { rotateDeg: 90, flipX: true, flipY: false, scale: 1, offsetX: 0, offsetY: 0 };

const SPRITE_SIZE = 280; // px, on-screen render size (source frames are 256x256)
// Her actual clickable button is padded well beyond the visual sprite size
// -- the walk-cycle art doesn't fill its 256x256 frame edge to edge, and
// stacking that with the per-frame translateY compensation could put part
// of her visible body outside a hit box sized exactly to SPRITE_SIZE,
// letting a click that looks like it landed on her pass through to
// whatever's underneath. Padding it out removes that dead zone entirely.
const HIT_PADDING = 40;
const CLIMB_FRAME_MS = 45;
// Was 70ms -- with only 6 splat frames that's 420ms total, so fast it read
// as an instant snap instead of a visible animation. Slowed down so each
// pose is actually perceivable.
const SPLAT_FRAME_MS = 220;
// Was a snappy 450ms -- read as an abrupt cutoff right after the last splat
// frame instead of a smooth wind-down. Stretched to 12 frame-durations
// worth of fade (matching the splat's own per-frame pacing) so she holds
// the puddle pose and gradually dissolves, instead of holding then vanishing.
const FADE_MS = 12 * SPLAT_FRAME_MS;
const CATCH_SOUND_SRC = '/glym/glymling-catch.mp3';
// The mp3 itself is mastered hot (peaks around -2 dB), so 0.4 played far too loud.
const CATCH_SOUND_VOLUME = 0.12;
const CLIMB_DURATION_S = 12;
const GOONCOIN_AMOUNT = 15;

// The actual overhang distance lives in globals.css (.glymling-climb-left /
// -right) since it has to match the CSS class, not a JS value — a clip
// against the edge is what sells "climbing the side of the screen" instead
// of just floating in open space, same idea as a sidebar pinned flush to
// the edge.

// How long after confirming she's eligible THIS VISIT before she actually
// climbs, in ms — randomized within this range so it doesn't read as "she
// appears the instant the page loads," and isn't something you can farm by
// sitting still. This is just in-session pacing now, not the real cadence:
// whether she's eligible to appear AT ALL today is a server-side ~12-day
// (twice a month) schedule per user (see /api/glymling/status and
// claim_glymling_eligibility) with catch-up -- if it's overdue, she's
// eligible on the next visit. A single long-lived tab used to be able to
// re-roll this window forever (every miss/catch just scheduled another one
// 6-15 min later, indefinitely); now eligibility is consumed for the whole
// ~12-day cycle the moment the status check confirms it, so she only ever
// shows up once per visit that's actually due.
const MIN_INTERVAL_MS = 6 * 60_000;
const MAX_INTERVAL_MS = 15 * 60_000;

type Phase = 'climbing' | 'splatting' | 'fading' | null;
// Locked to 'right' at spawn (see spawn()) -- she used to also spawn
// left-edge or drift toward the middle, which read as random/broken.
// Kept as a type (rather than dropping the concept entirely) since the
// frozen-on-catch position still needs to know which side her callout
// should be inward from.
type Edge = 'left' | 'right';

/**
 * "Catch Glym!" screen-wide Easter egg — a small Glymling sprite climbs
 * from the bottom of the screen to the top, pinned to the right viewport
 * edge (slightly overhanging it, like a sidebar), sitting above all other
 * content. Eligible roughly twice a month (~12 days apart) per user,
 * tracked server-side with catch-up (see /api/glymling/status) — not an
 * ephemeral client timer, so it has real memory across visits and can't
 * just keep re-triggering forever in one long-lived tab. Clicking her before she
 * reaches the top freezes her in place, plays a one-shot splat-and-fade
 * (reusing the death spritesheet's pop frames, since there's no dedicated
 * pop/explode animation), and awards Gooncoin server-side (see
 * /api/glymling/catch, which enforces the real daily cap — nothing about
 * the reward is trusted from this component). Missing her just lets her
 * climb off the top with no penalty and no splat; the splat is specifically
 * the catch reward.
 *
 * vh-based positioning (the CSS keyframe animates `bottom` in percent of
 * viewport height, not fixed pixels) is what makes this correctly scale to
 * any device height — the climb always spans the full screen regardless of
 * how tall the actual viewport is.
 *
 * super_admin gets a manual trigger button (bottom-left, always present)
 * to force a climb on demand for testing across pages, instead of waiting
 * out the real interval.
 */
export function GlymlingClimbEvent() {
  const t = useTranslations("glymlingClimbEvent")
  const { profile } = useAuthStore();
  const [loggedIn, setLoggedIn] = useState(false);
  const [phase, setPhase] = useState<Phase>(null);
  const [edge, setEdge] = useState<Edge>('right');
  const [frameIndex, setFrameIndex] = useState(0);
  const [reward, setReward] = useState<{ amount: number; awarded: boolean } | null>(null);
  // Set only at the moment of catch, to pin her exact on-screen spot — see
  // handleCatch. Null while climbing (the CSS keyframe + edge drive
  // position instead).
  const [frozenPos, setFrozenPos] = useState<{ top: number; left: number } | null>(null);
  // Bumped on every spawn (scheduled or manually triggered) and used as a
  // React key on the animated track, so a manual re-trigger mid-climb
  // forces a clean remount instead of trying to patch a running animation.
  const [runId, setRunId] = useState(0);
  // Holds her static and center-screen with the auto walk-cycle ticker
  // paused, and shows Prev/Next controls, so a frame that causes a visible
  // jolt in the real climb can be identified by eye instead of guessed at.
  const [frameTestMode, setFrameTestMode] = useState(false);
  // Same idea as frameTestMode but for the splat: holds a splat frame
  // static center-screen with live rotate/flip/scale/nudge controls, so
  // the right values can be found by eye instead of guessing blind through
  // a code-edit-screenshot loop every time.
  const [splatTestMode, setSplatTestMode] = useState(false);
  // The real, shipped values load from the DB (see the migration/route
  // above) if a super_admin has ever saved a tuning from the test panel,
  // falling back to SPLAT_ADJUST_DEFAULT until then -- so this same
  // GlymlingClimbEvent component works out of the box, and adjustments made
  // through the panel apply for every future viewer, not just this tab.
  const { adjust: splatAdjust, setAdjust: setSplatAdjust, save: saveSplatAdjust } = useSpriteTuning('glymling-splat', SPLAT_ADJUST_DEFAULT);
  // Off by default -- gated entirely on the "glymling_test_tools_enabled"
  // feature_flags row, toggled from Admin Settings -> Features -> Glym Test
  // Tools (components/admin/admin-settings.tsx), not a control rendered by
  // this component at all -- so there's zero floating UI footprint until an
  // admin deliberately flips it on there. Read as a raw flag row directly
  // (not through lib/feature-flags.ts's isFeatureEnabled/useFeatureFlag,
  // which auto-return true for any super_admin regardless of the flag's
  // real value -- that would make this toggle a no-op for the one account
  // it's meant to control). isSuperAdmin below is still the real security
  // boundary: the buttons only ever render at all once that's already true.
  const [showTestTools, setShowTestTools] = useState(false);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frameTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Flips true once every frame has actually finished loading -- see the
  // preload effect below. Spawning is gated on this (see spawnWhenReady)
  // so a genuinely cold visit (nothing cached, real network latency) can't
  // still race the fetch the way the fire-and-forget preload alone could.
  const framesReadyRef = useRef(false);
  // Loaded up front so the catch sound starts the instant she's tapped. Creating
  // it inside the click handler meant downloading + decoding the mp3 after the
  // tap, which is what made the sound arrive late.
  const catchSoundRef = useRef<HTMLAudioElement | null>(null);

  const isSuperAdmin = profile?.role === 'super_admin';

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    createBrowserClient()
      .from('feature_flags')
      .select('enabled')
      .eq('name', 'glymling_test_tools_enabled')
      .maybeSingle()
      .then(({ data }: { data: any }) => {
        if (!cancelled) setShowTestTools(!!data?.enabled);
      });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  // Cheap "is anyone logged in" probe reusing an existing real endpoint
  // rather than adding a new auth dependency just for this — a guest gets
  // no event at all, since there's no reward to give them.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/account/balances')
      .then((res) => { if (!cancelled) setLoggedIn(res.ok); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Warm the browser's cache for every frame up front. Without this, the
  // walk-cycle <img> swaps `src` every 45ms across 13 different frame URLs
  // -- on a first visit, none of them are cached yet, so the browser never
  // finishes fetching/decoding one frame before the next swap happens, and
  // she's invisible the whole climb (confirmed live: imgComplete was false,
  // naturalWidth 0, the entire time). Waiting for every image's real load
  // event (not just firing `new Image()` and hoping) is what lets
  // spawnWhenReady below actually know it's safe to show her -- on a slow
  // connection a fire-and-forget warm-up can still lose the race.
  useEffect(() => {
    let cancelled = false;
    const urls = [...CLIMB_FRAMES, ...SPLAT_FRAMES];
    Promise.all(
      urls.map(
        (src) =>
          new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = src;
          })
      )
    ).then(() => {
      if (!cancelled) framesReadyRef.current = true;
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const audio = new Audio(CATCH_SOUND_SRC);
    audio.preload = 'auto';
    audio.volume = CATCH_SOUND_VOLUME;
    audio.load();
    catchSoundRef.current = audio;
    return () => {
      catchSoundRef.current = null;
    };
  }, []);

  const spawn = () => {
    setEdge('right');
    setFrameIndex(0);
    setReward(null);
    setFrozenPos(null);
    setPhase('climbing');
    setRunId((n) => n + 1);
  };

  // Won't actually spawn her until every frame is confirmed loaded --
  // retries every 250ms in the (rare, cold-visit) case the preload hasn't
  // finished yet. Real climbs are already gated behind a several-minute
  // timer, so this adds no perceptible delay; it only protects the edge
  // case where a spawn would otherwise race a still-loading image.
  const spawnWhenReady = () => {
    if (!framesReadyRef.current) {
      timerRef.current = setTimeout(spawnWhenReady, 250);
      return;
    }
    spawn();
  };

  const scheduleNext = () => {
    const delay = MIN_INTERVAL_MS + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS);
    timerRef.current = setTimeout(spawnWhenReady, delay);
  };

  // Checks the server-side ~12-day schedule once per visit -- this call
  // itself atomically claims eligibility and pushes the next spawn date out
  // another 12 days if she's due, so a second tab (or a re-render) can't
  // also see "eligible" and double-schedule her. Only ever fires
  // scheduleNext() once per mount; see the removed "reschedule forever"
  // effect note below for why there's no retry loop here.
  useEffect(() => {
    if (!loggedIn) return;
    let cancelled = false;
    fetch('/api/glymling/status')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.eligible) scheduleNext();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  // Walk-cycle frame ticker while climbing; splat frame ticker while
  // splatting. Reaching the last splat frame hands off to the fade phase.
  useEffect(() => {
    if (frameTestMode || splatTestMode) return;
    if (phase !== 'climbing' && phase !== 'splatting') return;
    const frames = phase === 'climbing' ? CLIMB_FRAMES : SPLAT_FRAMES;
    const stepMs = phase === 'climbing' ? CLIMB_FRAME_MS : SPLAT_FRAME_MS;
    frameTimerRef.current = setInterval(() => {
      setFrameIndex((i) => {
        const next = i + 1;
        if (phase === 'splatting' && next >= frames.length) {
          if (frameTimerRef.current) clearInterval(frameTimerRef.current);
          setPhase('fading');
          return i; // hold the last splat frame while it fades
        }
        return next % frames.length;
      });
    }, stepMs);
    return () => { if (frameTimerRef.current) clearInterval(frameTimerRef.current); };
  }, [phase, runId, frameTestMode, splatTestMode]);

  // Climb finished on its own (reached the top, never caught) — just vanish,
  // no splat/fade for a miss. Suppressed in frame test mode, which holds
  // her in place indefinitely until Exit is clicked.
  useEffect(() => {
    if (phase !== 'climbing' || frameTestMode) return;
    const timeout = setTimeout(() => setPhase(null), CLIMB_DURATION_S * 1000);
    return () => clearTimeout(timeout);
  }, [phase, runId, frameTestMode]);

  // Fade phase: let the opacity transition actually run before unmounting —
  // setting phase to null immediately would skip straight past the CSS
  // transition since the element stops rendering that same tick.
  useEffect(() => {
    if (phase !== 'fading') return;
    const timeout = setTimeout(() => setPhase(null), FADE_MS);
    return () => clearTimeout(timeout);
  }, [phase, runId]);

  // No "reschedule the next appearance" effect here on purpose -- there
  // used to be one that fired scheduleNext() again every time phase
  // returned to null, which meant a single long-lived tab would keep
  // re-triggering her forever, every 6-15 minutes, all day. Eligibility is
  // now a one-shot server-claimed ~12-day cycle (see the status-check
  // effect above), consumed the moment it's confirmed -- so once she's
  // shown up (caught or missed, same either way) that's this cycle done
  // until the next real visit after the schedule advances.

  const handleCatch = () => {
    if (phase !== 'climbing') return;
    // Freeze her exactly where she is right now — read the live animated
    // position before switching off the CSS animation, otherwise she'd jump
    // to some default spot instead of splatting where she was actually caught.
    const rect = trackRef.current?.getBoundingClientRect();
    if (rect) setFrozenPos({ top: rect.top, left: rect.left });
    setPhase('splatting');
    setFrameIndex(0);
    // Reuse the preloaded element (rewound) so it plays with no delay; fall back
    // to a fresh one if the preload somehow isn't there. play() can reject on
    // some browsers/autoplay policies -- never let that break the actual catch.
    const catchSound = catchSoundRef.current ?? new Audio(CATCH_SOUND_SRC);
    catchSound.volume = CATCH_SOUND_VOLUME;
    catchSound.currentTime = 0;
    catchSound.play().catch(() => {});
    fetch('/api/glymling/catch', { method: 'POST' })
      .then((res) => res.json())
      .then((data) => setReward({ amount: data.gooncoinAwarded ?? 0, awarded: !!data.awarded }))
      .catch(() => setReward({ amount: 0, awarded: false }));
  };

  const handleManualTrigger = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setFrameTestMode(false);
    setSplatTestMode(false);
    spawnWhenReady();
  };

  const handleFrameTest = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!framesReadyRef.current) {
      timerRef.current = setTimeout(handleFrameTest, 250);
      return;
    }
    setSplatTestMode(false);
    setFrameTestMode(true);
    setEdge('right');
    setFrameIndex(0);
    setReward(null);
    setFrozenPos(null);
    setPhase('climbing');
    setRunId((n) => n + 1);
  };

  const exitFrameTest = () => {
    setFrameTestMode(false);
    setPhase(null);
  };

  const stepFrame = (delta: number) => {
    setFrameIndex((i) => (i + delta + CLIMB_FRAME_COUNT) % CLIMB_FRAME_COUNT);
  };

  const handleSplatTest = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!framesReadyRef.current) {
      timerRef.current = setTimeout(handleSplatTest, 250);
      return;
    }
    setFrameTestMode(false);
    setSplatTestMode(true);
    setEdge('right');
    setFrameIndex(0);
    setReward(null);
    setFrozenPos(null);
    setPhase('splatting');
    setRunId((n) => n + 1);
  };

  const exitSplatTest = () => {
    setSplatTestMode(false);
    setPhase(null);
  };

  const frames = phase === 'climbing' ? CLIMB_FRAMES : SPLAT_FRAMES;
  const src = frames[Math.min(frameIndex, frames.length - 1)];
  const frozen = frozenPos !== null;
  const climbYOffsetPx = CLIMB_FRAME_Y_OFFSET[Math.min(frameIndex, CLIMB_FRAME_Y_OFFSET.length - 1)] * (SPRITE_SIZE / 256);
  const splatTransform = spriteAdjustToTransform(splatAdjust);

  return (
    <div className="pointer-events-none fixed inset-0 z-[9999]" aria-hidden={phase !== 'climbing'}>
      {/* Neutral dark backdrop for the test tools only -- the preview spot
          (30% top, 50% left) happens to land exactly on the Featured
          carousel, and Glym's own portrait card there is a similar purple
          to her sprite, which fully camouflaged her during testing (she
          was actually rendering correctly the whole time). Real gameplay
          never uses this centered spot, so it's test-only. */}
      {(frameTestMode || splatTestMode) && (
        <div className="absolute inset-0 bg-black/70" />
      )}
      {phase !== null && (
        <div
          key={runId}
          ref={trackRef}
          className={!frozen && !frameTestMode && !splatTestMode ? `glymling-climb-track glymling-climb-${edge}` : ''}
          style={{
            position: 'fixed',
            // Fixed to the sprite's own size, not shrink-wrapped around its
            // widest child -- the callout used to be wider than the sprite
            // and in-flow, which silently widened this box and threw off
            // the edge math below (the box's edge lined up, not the
            // sprite's). Now the callout is absolutely positioned outside
            // this box instead, so it can never affect its width again.
            width: SPRITE_SIZE,
            height: SPRITE_SIZE,
            ...(frozen
              ? { top: frozenPos!.top, left: frozenPos!.left }
              : frameTestMode || splatTestMode
              ? { top: '30%', left: '50%', transform: 'translate(-50%, -50%)' }
              : { animationDuration: `${CLIMB_DURATION_S}s` }),
          }}
        >
          <div className="relative h-full w-full">
            {phase === 'climbing' && !frameTestMode && (
              <button
                type="button"
                onClick={handleCatch}
                aria-label={t("catchGlymForAGooncoinReward")}
                style={{ backgroundColor: 'rgb(220, 215, 242)' }}
                className={`pointer-events-auto absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-full px-3.5 py-2 text-center text-base font-bold leading-tight text-primary shadow-lg animate-bounce ${edge === 'right' ? 'right-full -mr-4' : 'left-full -ml-4'}`}
              >
                {t("catchGlymBang")}
                <br />
                +{GOONCOIN_AMOUNT} <img src="/gooncoin/gooncoin_t1.webp" alt="" className="ml-0.5 inline h-4 w-4 align-[-2px]" />
              </button>
            )}
            {reward && (
              // Same pill design as the pre-catch callout above (light
              // lavender background, dark text) instead of a plain
              // drop-shadowed span -- that was unreadable against a lot of
              // the page's dark card art. A bigger spinning coin sits above
              // the text on an actual award (not on the "already caught"
              // case, which has nothing to celebrate), and the whole pill
              // fades out with her on the same linear/FADE_MS transition
              // instead of just vanishing when she does.
              <div
                // The pop animation only owns `transform` while it's playing
                // (see the CSS comment on .glymling-reward-pop) -- once it
                // releases control, this plain inline transform is what
                // keeps the pill vertically centered.
                style={{ backgroundColor: 'rgb(220, 215, 242)', transitionDuration: `${FADE_MS}ms`, transform: 'translateY(-50%)' }}
                className={`glymling-reward-pop pointer-events-none absolute top-1/2 flex flex-col items-center gap-1 whitespace-nowrap rounded-2xl px-3.5 py-2.5 text-center shadow-lg transition-opacity linear ${edge === 'right' ? 'right-full -mr-4' : 'left-full -ml-4'} ${phase === 'fading' ? 'opacity-0' : 'opacity-100'}`}
              >
                {reward.awarded && (
                  <img src="/gooncoin/gooncoin_spin.gif" alt="" className="h-12 w-12" />
                )}
                <span className="text-base font-bold leading-tight text-primary">
                  {reward.awarded ? `+${reward.amount} Gooncoin!` : 'Already caught today!'}
                </span>
              </div>
            )}
            {/* Padded well past the visual sprite (see HIT_PADDING) so the
                clickable area is always bigger than what's on screen, never
                smaller -- was letting clicks that looked like they landed
                on her pass through to the page underneath. */}
            <button
              type="button"
              onClick={handleCatch}
              aria-label={t("catchGlym")}
              // No `disabled` attribute -- the browser's own default styling
              // for a disabled control was fighting our opacity transition
              // (measured live: it plateaued at a hard 0.6 instead of
              // continuing to 0). pointer-events-none below already blocks
              // clicks outside the climbing phase, and handleCatch itself
              // still guards on `phase !== 'climbing'`, so nothing is
              // actually clickable here that wasn't before.
              //
              // linear, not ease-out -- ease-out's slow tail near the end
              // meant the fixed FADE_MS timeout (which unmounts her) fired
              // before the eased curve visually finished dropping to 0, so
              // she seemed to vanish still partly visible instead of fading
              // all the way out. Linear reaches 0 exactly at FADE_MS.
              className={`absolute flex items-center justify-center transition-opacity linear ${phase === 'climbing' && !frameTestMode ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'} ${phase === 'fading' ? 'opacity-0' : 'opacity-100'}`}
              style={{ inset: -HIT_PADDING, transitionDuration: `${FADE_MS}ms` }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element -- fixed-size sprite frames swapped many times/sec; next/image's overhead isn't worth it here */}
              <img
                src={src}
                alt=""
                className="select-none object-contain"
                draggable={false}
                style={{
                  width: SPRITE_SIZE,
                  height: SPRITE_SIZE,
                  // splatTransform is built from splatAdjust (see its
                  // definition above) -- SPLAT_ADJUST_DEFAULT is the real
                  // shipped values; a super_admin can live-preview changes
                  // to them via the "Test Glym splat" panel below.
                  transform:
                    phase === 'climbing'
                      ? `translateY(${climbYOffsetPx}px)`
                      : phase === 'splatting' || phase === 'fading'
                      ? splatTransform
                      : undefined,
                }}
              />
            </button>
          </div>
        </div>
      )}
      {isSuperAdmin && loggedIn && showTestTools && (
        <div className="pointer-events-auto fixed bottom-3 left-3 z-[9999] flex flex-col items-start gap-1.5">
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={handleManualTrigger}
              className="rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-semibold text-white shadow-lg hover:bg-black/85"
            >
              {t("triggerGlymClimb")}
            </button>
            <button
              type="button"
              onClick={handleFrameTest}
              className="rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-semibold text-white shadow-lg hover:bg-black/85"
            >
              {t("testGlymFrames")}
            </button>
            <button
              type="button"
              onClick={handleSplatTest}
              className="rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-semibold text-white shadow-lg hover:bg-black/85"
            >
              {t("testGlymSplat")}
            </button>
          </div>
          {frameTestMode && phase === 'climbing' && (
            <div className="flex items-center gap-2 rounded-lg bg-black/85 px-3 py-2 text-white shadow-lg">
              <button type="button" onClick={() => stepFrame(-1)} className="rounded bg-white/15 px-2 py-1 text-xs font-semibold hover:bg-white/25">
                {t("prev")}
              </button>
              <span className="whitespace-nowrap font-mono text-xs">
                Frame {frameIndex + 1}/{CLIMB_FRAME_COUNT} — frame{String(frameIndex + 1).padStart(2, '0')}.webp
              </span>
              <button type="button" onClick={() => stepFrame(1)} className="rounded bg-white/15 px-2 py-1 text-xs font-semibold hover:bg-white/25">
                {t("next")}
              </button>
              <button type="button" onClick={exitFrameTest} className="ml-1 rounded bg-red-600 px-2 py-1 text-xs font-semibold hover:bg-red-500">
                {t("exit")}
              </button>
            </div>
          )}
          {splatTestMode && phase === 'splatting' && (
            <SpriteTransformTesterPanel
              title={t("glymSplat")}
              adjust={splatAdjust}
              onAdjustChange={setSplatAdjust}
              defaultAdjust={SPLAT_ADJUST_DEFAULT}
              frameCount={SPLAT_FRAMES.length}
              frameIndex={frameIndex}
              onFrameIndexChange={setFrameIndex}
              frameLabel={(i) => `frame${String([12, 13, 14, 15, 16, 17][Math.min(i, 5)]).padStart(2, '0')}.webp`}
              onSave={saveSplatAdjust}
              onExit={exitSplatTest}
            />
          )}
        </div>
      )}
    </div>
  );
}
